import { useEffect, useState } from 'react';
import { usePersonalInfoStore } from '../../../../stores/usePersonalInfoStore';
import type { PhoneNumberUpdateEntry, StreetAddressUpdateEntry } from '../../../../types/api';
import { getIsImpersonating } from '../../../../utils/session';

export interface PersonalInfoSectionProps {
  customerServicePhone: string;
  onClose: () => void;
}

interface RequiredFieldFlags {
  areaCode: boolean;
  phoneNumber: boolean;
  street1: boolean;
  city: boolean;
  state: boolean;
  zip: boolean;
}

function isBlank(value: string | undefined): boolean {
  return !value || value.trim() === '';
}

// VSDP-5988: after a blocked save attempt, flag which currently-visible
// required fields are blank so the form can outline them (Street 2 excluded).
function getRequiredFieldFlags(
  attemptedSave: boolean,
  phone: PhoneNumberUpdateEntry | undefined,
  address: StreetAddressUpdateEntry | undefined
): RequiredFieldFlags {
  return {
    areaCode: attemptedSave && isBlank(phone?.AreaCode),
    phoneNumber: attemptedSave && isBlank(phone?.PhoneNumber),
    street1: attemptedSave && isBlank(address?.Street1),
    city: attemptedSave && isBlank(address?.City),
    state: attemptedSave && isBlank(address?.State),
    zip: attemptedSave && isBlank(address?.Zip),
  };
}

export function PersonalInfoSection({ customerServicePhone, onClose }: PersonalInfoSectionProps) {
  const {
    firstName,
    lastName,
    dateOfBirth,
    emailAddress,
    phoneNumbers,
    selectedPhoneTypeId,
    addresses,
    selectedAddressTypeId,
    addressTypes,
    stateOptions,
    isLoading,
    isSaving,
    error,
    successMessage,
    loadFromSession,
    loadAddressTypes,
    loadStates,
    updatePhoneEntry,
    updateAddressField,
    setSelectedPhoneTypeId,
    setSelectedAddressTypeId,
    savePersonalInfo,
    clearMessages,
  } = usePersonalInfoStore();

  const isImpersonating = getIsImpersonating();

  // Tracks whether the user has attempted a save this session — required-field
  // highlighting (VSDP-5988) only appears after a blocked attempt, matching the
  // legacy AngularJS form's behavior of not flagging fields before submission.
  const [attemptedSave, setAttemptedSave] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: store functions are stable Zustand references — not reactive dependencies
  useEffect(() => {
    // usePersonalInfoStore is a module-level singleton, so a stale error/success
    // message from a prior save can otherwise keep showing indefinitely across
    // close/reopen — clear it whenever the section is freshly mounted.
    clearMessages();
    loadFromSession();
    void loadAddressTypes();
    void loadStates();
  }, []);

  useEffect(() => {
    if (successMessage) {
      setAttemptedSave(false);
    }
  }, [successMessage]);

  const selectedPhone = phoneNumbers.find(p => p.PhoneTypeId === selectedPhoneTypeId);
  const selectedAddress = addresses.find(a => a.AddressTypeId === selectedAddressTypeId);

  const {
    areaCode: areaCodeInvalid,
    phoneNumber: phoneNumberInvalid,
    street1: street1Invalid,
    city: cityInvalid,
    state: stateInvalid,
    zip: zipInvalid,
  } = getRequiredFieldFlags(attemptedSave, selectedPhone, selectedAddress);

  const handleSave = () => {
    clearMessages();
    setAttemptedSave(true);
    void savePersonalInfo();
  };

  const handleClose = () => {
    clearMessages();
    onClose();
  };

  return (
    <div className="personal-info-section">
      {/* <h4>Personal Information</h4>
      <hr /> */}

      {/* Error banner */}
      {error && (
        <div className="alert alert-danger">
          {error} For assistance, call{' '}
          <strong>{customerServicePhone}</strong>.
        </div>
      )}

      {isLoading && (
        <div className="text-center" style={{ padding: '10px 0' }}>
          <span>Loading…</span>
        </div>
      )}

      {/* Read-only identity fields — two rows, two columns */}
      <div className="row">
        <div className="col-xs-6">
          <div className="form-group">
            <span className="control-label">First Name</span>
            <p className="form-control-static">{firstName || '—'}</p>
          </div>
        </div>
        <div className="col-xs-6">
          <div className="form-group">
            <span className="control-label">Last Name</span>
            <p className="form-control-static">{lastName || '—'}</p>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-6">
          <div className="form-group">
            <span className="control-label">Email Address</span>
            <p className="form-control-static" style={{ wordBreak: 'break-word' }}>{emailAddress || '—'}</p>
          </div>
        </div>
        <div className="col-xs-6">
          <div className="form-group">
            <span className="control-label">Date of Birth</span>
            <p className="form-control-static">
              {dateOfBirth ? (dateOfBirth.split('T')[0].split(' ')[0]) : '—'}
            </p>
          </div>
        </div>
      </div>

      <hr />

      {/* Phone Type and Address fields positioned side by side per VSDP-5888 mockup */}
      <div className="row">
        <div className="col-xs-6">
          {/* Phone Numbers */}
          {phoneNumbers.length > 0 && (
            <>
              <div className="form-group">
                <label htmlFor="pi-phone-type">Phone Type</label>
                <select
                  id="pi-phone-type"
                  className="form-control custom"
                  value={selectedPhoneTypeId ?? ''}
                  onChange={e => setSelectedPhoneTypeId(Number(e.target.value))}
                  disabled={isSaving}
                >
                  {phoneNumbers.map(p => (
                    <option key={p.PhoneTypeId} value={p.PhoneTypeId}>
                      {p.PhoneTypeDescr || `Type ${p.PhoneTypeId}`}
                    </option>
                  ))}
                </select>
              </div>
              {selectedPhone && (
                <div className="row">
                  <div className="col-xs-4">
                    <div className={`form-group${areaCodeInvalid ? ' has-error' : ''}`}>
                      <label htmlFor="pi-area-code" className="sr-only">
                        Area Code
                      </label>
                      <input
                        id="pi-area-code"
                        className="form-control"
                        type="text"
                        placeholder="Area Code"
                        value={selectedPhone.AreaCode}
                        maxLength={3}
                        onChange={e =>
                          updatePhoneEntry(selectedPhone.PhoneTypeId, 'AreaCode', e.target.value.replace(/\D/g, ''))
                        }
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                  <div className="col-xs-8">
                    <div className={`form-group${phoneNumberInvalid ? ' has-error' : ''}`}>
                      <label htmlFor="pi-phone-number" className="sr-only">
                        Phone Number
                      </label>
                      <input
                        id="pi-phone-number"
                        className="form-control"
                        type="text"
                        placeholder="Phone Number"
                        value={selectedPhone.PhoneNumber}
                        maxLength={7}
                        onChange={e =>
                          updatePhoneEntry(
                            selectedPhone.PhoneTypeId,
                            'PhoneNumber',
                            e.target.value.replace(/\D/g, '')
                          )
                        }
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="col-xs-6">
          {/* Addresses */}
          {!isLoading && addressTypes.length > 0 && (
            <>
              <div className="form-group">
                <label htmlFor="pi-address-type">Address Type</label>
                <select
                  id="pi-address-type"
                  className="form-control custom"
                  value={selectedAddressTypeId ?? ''}
                  onChange={e => setSelectedAddressTypeId(Number(e.target.value))}
                  disabled={isSaving}
                >
                  {addressTypes.map(t => {
                    const id = t.AddressTypeID ?? t.AddressTypeId;
                    return (
                      <option key={id} value={id}>
                        {t.AddressTypeName}
                      </option>
                    );
                  })}
                </select>
              </div>

              {selectedAddress && (
                <>
                  <div className={`form-group${street1Invalid ? ' has-error' : ''}`}>
                    <label htmlFor="pi-street1">Street 1</label>
                    <input
                      id="pi-street1"
                      className="form-control"
                      type="text"
                      value={selectedAddress.Street1}
                      maxLength={50}
                      onChange={e =>
                        updateAddressField(selectedAddressTypeId!, 'Street1', e.target.value)
                      }
                      disabled={isSaving}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="pi-street2">Street 2</label>
                    <input
                      id="pi-street2"
                      className="form-control"
                      type="text"
                      value={selectedAddress.Street2 ?? ''}
                      maxLength={50}
                      onChange={e =>
                        updateAddressField(selectedAddressTypeId!, 'Street2', e.target.value)
                      }
                      disabled={isSaving}
                    />
                  </div>
                  <div className={`form-group${cityInvalid ? ' has-error' : ''}`}>
                    <label htmlFor="pi-city">City</label>
                    <input
                      id="pi-city"
                      className="form-control"
                      type="text"
                      value={selectedAddress.City}
                      maxLength={20}
                      onChange={e =>
                        updateAddressField(selectedAddressTypeId!, 'City', e.target.value)
                      }
                      disabled={isSaving}
                    />
                  </div>
                  <div className="row">
                    <div className="col-xs-6">
                      <div className={`form-group${stateInvalid ? ' has-error' : ''}`}>
                        <label htmlFor="pi-state">State</label>
                        <select
                          id="pi-state"
                          className="form-control custom"
                          value={selectedAddress.State}
                          onChange={e =>
                            updateAddressField(selectedAddressTypeId!, 'State', e.target.value)
                          }
                          disabled={isSaving}
                        >
                          <option value="">Pick a state</option>
                          {stateOptions.map(s => (
                            <option key={s.StateAbbr} value={s.StateAbbr}>
                              {s.StateName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-6">
                      <div className={`form-group${zipInvalid ? ' has-error' : ''}`}>
                        <label htmlFor="pi-zip">Zip</label>
                        <input
                          id="pi-zip"
                          className="form-control"
                          type="text"
                          value={selectedAddress.Zip}
                          maxLength={5}
                          onChange={e =>
                            updateAddressField(
                              selectedAddressTypeId!,
                              'Zip',
                              e.target.value.replace(/\D/g, '')
                            )
                          }
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Success banner */}
      {successMessage && (
        <div className="alert alert-success" style={{ marginTop: 10 }}>
          <strong>{successMessage}</strong>
        </div>
      )}

      {/* Actions */}
      <div className="form-group" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="btn btn-default btn-sm"
          style={{ backgroundColor: '#FF471A', color: '#fff', borderRadius: 4, border: 'none' }}
          onClick={handleClose}
          disabled={isSaving}
        >
          Cancel
        </button>
        {'  '}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          style={{ float: 'right' }}
          onClick={handleSave}
          disabled={isSaving || isLoading || isImpersonating}
        >
          {isSaving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
